import React, { useEffect, useState, createContext, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Modal, Form, Input, Button, message } from 'antd';
import { registerUser, loginUser } from '../api/userAPI';
import '../styles/FooterNav.css';

const PHONE_11_DIGIT_REGEX = /^\d{11}$/;
const normalizePhone = (phone) => String(phone ?? '').trim();
const isValidPhone = (phone) => PHONE_11_DIGIT_REGEX.test(normalizePhone(phone));

export const FooterNavContext = createContext();

const FooterNav = () => {
  const [username, setUsername] = useState(null);
  // const [isLoginModalVisible, setLoginModalVisible] = useState(false);
  const { isLoginModalVisible, setLoginModalVisible } = useContext(FooterNavContext);
  const [isRegisterModalVisible, setRegisterModalVisible] = useState(false);
  // const { setLoginModalVisible } = useContext(FooterNavContext);

  const [form] = Form.useForm();
  const [registerForm] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation(); // 获取当前地址信息

  // 从地址栏路径获取当前激活的导航项（补充 groupOverview / knockout -> 归入“赛事”）
  const getActiveTab = (pathname) => {
    const p = String(pathname || '').toLowerCase();
    if (p.startsWith('/home')) {
      return 'home';
    } else if (
      p.startsWith('/match') ||
      p.startsWith('/schedule') ||
      p.startsWith('/record') ||
      p.startsWith('/groupoverview') ||
      p.startsWith('/knockout') ||
      p.startsWith('/draw')
    ) {
      return 'matches';
    } else if (
      p.startsWith('/user') ||
      p.startsWith('/manage')
    ) {
      return 'user';
    } else {
    return 'home'; // 默认选中主页
    }
  };
  const activeTab = getActiveTab(location.pathname);

  useEffect(() => {
    const storedUsername = localStorage.getItem('username');
    setUsername(storedUsername);
  }, []);

  // —— 登陆确认：仅手机号 + 密码 —— //
  const handleOk = () => {
    form.validateFields().then((values) => {
      // 仅支持：手机号 + 密码
      const { phone, password } = values || {};
      if (!phone || !password) {
        message.error('手机号和密码不能为空');
        return;
      }
      // 兜底校验：必须全为数字
      if (!/^\d+$/.test(String(phone))) {
        message.warning('仅支持手机号+密码登陆');
        return;
      }
      // 后端已改为仅支持 phone + password
      loginUser({ phone, password }).then((data) => {
        message.success('登陆成功');
        localStorage.setItem('username', data.username);
        localStorage.setItem('userID', data.id);
        setUsername(data.username);
        setLoginModalVisible(false);
        // 设置登录过期时间
        const now = new Date();                 // 当前本地时间
        const expireAt4am = new Date(now);      // 先拷贝一份
        expireAt4am.setDate(now.getDate() + 14); // 两周后
        expireAt4am.setHours(4, 0, 0, 0);       // 调到 04:00:00.000
        localStorage.setItem('loginExpiry', expireAt4am.getTime()); // 以时间戳形式保存
        // 1秒后 reload 当前页面
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }).catch((error) => {
        message.error(error.response.data.error);
      });
    }).catch((errorInfo) => {
      console.error('登陆验证失败:', errorInfo);
      message.error('请填写必要信息');
    });
  };

  const handleCancel = () => {
    setLoginModalVisible(false);
    // setLoginModalVisible(false); // 隐藏父组件控制的 Modal
  };

  // 处理注册逻辑
  const handleRegisterOk = () => {
    registerForm.validateFields().then((values) => {
      const normalizedPhone = normalizePhone(values.phone);
      if (!isValidPhone(normalizedPhone)) {
        message.error('请检查输入的手机号');
        return;
      }

      if (values.password !== values.confirmPassword) {
        message.error('两次输入的密码不一致');
        return;
      }

      const registerPayload = { ...values, phone: normalizedPhone };
      registerUser(registerPayload).then(() => {
        message.success('注册成功，请登录');
        setRegisterModalVisible(false);
        setLoginModalVisible(true);
      }).catch((error) => {
        const backendError = error?.response?.data?.error || '';
        if (
          backendError.includes('手机号已注册') ||
          backendError.includes('该手机号已注册过') ||
          backendError.includes("for key 'users.phone'")
        ) {
          message.error('该手机号已注册');
          return;
        }
        if (backendError.includes('手机号')) {
          message.error('请检查输入的手机号');
          return;
        }
        message.error(backendError || '注册失败');
        console.error(error);
      });
    }).catch((errorInfo) => {
      console.error('注册验证失败:', errorInfo);
      message.error('请填写必要信息');
    });
  };

  return (
    <div className="footer-nav-placeholder">
      <div className="footer-nav">
        <div
          className={`nav-item ${activeTab === 'home' ? 'active' : ''}`}
          onClick={() => navigate('/home')}
        >
          <div className="nav-icon">⛰️</div>
          <div className="nav-label">主页</div>
        </div>
        <div
          className={`nav-item ${['matches', 'groupOverview', 'knockout'].includes(activeTab) ? 'active' : ''}`}
          onClick={() => navigate('/matchList')}
        >
          <div className="nav-icon">🥇</div>
          <div className="nav-label">赛事</div>
        </div>
        <div
          className={`nav-item ${activeTab === 'user' ? 'active' : ''}`}
          onClick={() => {
            if (username) {
              navigate('/user');
            } else {
              setLoginModalVisible(true);
            }
          }}
        >
          <div className="nav-icon">🫆</div>
          <div className="nav-label">个人</div>
        </div>
  
        <Modal
          title="用户登陆"
          open={isLoginModalVisible}
          maskClosable={false}   // ✅ 禁止点遮罩关闭，避免“关闭→又打开”的击穿现象
          onCancel={handleCancel}
          style={{ top: '20vh' }}
          // 自定义底部：橙色“注册” + 主按钮“确认登陆”
          footer={[
            // —— 橙色“注册”按钮：复用原来“没有账号？点此注册”的点击行为 —— //
            <Button
              key="register"
              onClick={() => { setLoginModalVisible(false); setRegisterModalVisible(true); }}
              style={{ backgroundColor: 'rgb(211, 113, 0)', borderColor: 'rgb(211, 113, 0)', color: '#fff' }}
            >
              注册账号
            </Button>,
            <Button key="submit" type="primary" onClick={handleOk}>
              确认登陆
            </Button>
          ]}
        >
          <Form layout="vertical" form={form} initialValues={{ role: 'user' }}
                style={{ overflowY: 'auto', maxHeight: '65vh' }}>
            {/* —— 改为仅支持“手机号” —— */}
            <Form.Item
              label="手机号"
              name="phone"
              rules={[
                { required: true, message: '请输入手机号' },
                { pattern: /^\d+$/, message: '仅支持手机号+密码登陆' }  // 非数字即提示
              ]}
            >
              {/* inputMode='numeric' 便于移动端唤起数字键盘 */}
              <Input placeholder="请输入您的手机号" inputMode="numeric" />
            </Form.Item>
            <Form.Item label="密码" name="password" rules={[{ required: true, message: '请输入登陆密码' }]}>
              <Input.Password placeholder='请输入登陆密码'/>
            </Form.Item>
          </Form>
          {/* —— 文本链接改为“忘记密码”，仅给出管理员提示 —— */}
          <div style={{ marginTop: '10px', textAlign: 'center' }}>
            <Link
              to="#"
              onClick={() => message.warning('请您联系管理员确认身份')}
              style={{ color: '#1890ff', fontStyle: 'italic' }}
            >
              忘记密码
            </Link>
          </div>
          {/* <div style={{ marginTop: '40px', textAlign: 'center', marginBottom: '30px', width: '50%', alignContent: 'center', marginLeft: '25%' }}>
          <Button type="default" block style={{ backgroundColor: "#07c160", color: "white" }} onClick={handleWeChatLogin}>
              使用微信注册/登录
          </Button>
          </div> */}
        </Modal>

        {/* 注册模态框 */}
        <Modal
          title="用户注册"
          open={isRegisterModalVisible}
          maskClosable={false}   // ✅ 禁止点遮罩关闭，避免“关闭→又打开”的击穿现象
          onOk={handleRegisterOk}
          onCancel={() => setRegisterModalVisible(false)}
          okText="确认注册"
          cancelText="返回登录"
          style={{ top: '6vh' }}
          footer={[
            <Button key="back" onClick={() => { setRegisterModalVisible(false); setLoginModalVisible(true); }}>
              返回登录
            </Button>,
            <Button key="submit" type="primary" onClick={handleRegisterOk}>
              确认注册
            </Button>,
          ]}
        >
          <Form form={registerForm} layout="vertical"
                style={{ overflowY: 'auto', maxHeight: '65vh' }}>
            <Form.Item label="用户名" name="username" rules={[{ required: true, message: '请输入用户名' }]}>
              <Input placeholder='请输入您的用户名'/>
            </Form.Item>
            <Form.Item
              label="手机号"
              name="phone"
              rules={[
                { required: true, message: '请输入手机号' },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    if (isValidPhone(value)) return Promise.resolve();
                    return Promise.reject(new Error('请检查输入的手机号'));
                  }
                }
              ]}
            >
              <Input placeholder='请输入您的手机号' inputMode="numeric" maxLength={11} />
            </Form.Item>
            <Form.Item label="密码" name="password" rules={[{ required: true, message: '请输入密码' }]}>
              <Input.Password placeholder='请输入您的密码'/>
            </Form.Item>
            <Form.Item label="确认密码" name="confirmPassword" rules={[{ required: true, message: '请确认密码' }]}>
              <Input.Password placeholder='请确认您的密码'/>
            </Form.Item>
            <Form.Item label="邮箱(选填)" name="email">
              <Input placeholder='请输入您的邮箱'/>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default FooterNav;
